<template>
  <Teleport to="body">
    <Transition
      enter-active-class="slideInLeft"
      leave-active-class="slideOutLeft"
    >
      <div
        ref="drawerRef"
        class="fixed inset-0 h-dvh min-h-0 flex flex-col justify-between shadow bg-white w-full border-r border-r-gray-200 md:hidden z-200"
        v-if="appStore.showDrawer"
      >
        <div>
          <div
            class="h-12 flex items-center justify-between px-5 border-b border-b-gray-100"
          >
            <div></div>
            <img
              src="@/assets/images/medium_logo.webp"
              alt="Medium Logo"
              class="h-9"
            />
            <div>
              <button
                @click="appStore.closeDrawer"
                type="button"
                class="flex items-center justify-center"
              >
                <Icon name="tabler:x" size="22" />
              </button>
            </div>
          </div>
          <div class="px-5 pb-2.5 space-y-5 mt-5">
            <button
              class="flex items-center space-x-2 w-full transition-all duration-300 text-sm"
              @click="navigate(item)"
              type="button"
              v-for="item in navItems"
              :key="item.title"
              :name="$t(`nav.links.${item.key}`)"
              :aria-label="$t(`nav.links.${item.key}`)"
              :title="$t(`nav.links.${item.key}`)"
            >
              <Icon
                class="text-gray-400"
                :name="item.icon"
                weight="light"
                size="20"
              />
              <span>{{ $t(`nav.links.${item.key}`) }}</span>
            </button>
          </div>
        </div>

        <div class="flex flex-col items-start px-5 space-y-5 py-5">
          <div class="w-full space-y-5">
            <button
              @click="navigateNoAction('/contact')"
              class="flex items-center space-x-2 w-full transition-all duration-300 text-sm"
            >
              <Icon
                class="text-gray-400"
                name="tabler:mail-filled"
                size="20"
                weight="light"
              />
              <span>{{ $t(`nav.links.contact_us`) }}</span>
            </button>
            <NuxtLink
              class="flex items-center space-x-2 w-full transition-all duration-300 text-sm"
              target="_blank"
              :to="$localePath('/privacy')"
            >
              <Icon
                class="text-gray-400"
                name="tabler:shield-check-filled"
                size="20"
                weight="light"
              />
              <span>{{ $t(`nav.links.privacy`) }}</span>
            </NuxtLink>
            <NuxtLink
              class="flex items-center space-x-2 w-full transition-all duration-300 text-sm"
              target="_blank"
              :to="$localePath('/terms')"
            >
              <Icon
                class="text-gray-400"
                name="tabler:help-circle-filled"
                size="20"
                weight="light"
              />
              <span>{{ $t(`nav.links.terms`) }}</span>
            </NuxtLink>

            <MobileLanguageSelect />
            <BaseButton
              v-if="user"
              size="md"
              :label="$t('nav.user.logout')"
              variant="white"
              class="w-full"
              @click="authStore.signoutUser"
              :loading="authStore.signinOut"
            >
            </BaseButton>
            <div class="w-full" v-else>
              <BaseButton
                size="md"
                class="w-full"
                v-if="!user"
                :label="$t('auth.register.register')"
                variant="secondary"
                @click="navigateNoAction('/auth/register')"
              />
              <BaseButton
                size="md"
                class="w-full mt-2.5"
                variant="blue"
                v-if="!user"
                :label="$t('auth.login.login')"
                @click="navigateNoAction('/auth/login')"
              />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, shallowRef } from "vue";
import type { AppUser } from "~~/types/user";
import { onClickOutside } from "@vueuse/core";
import type { MenuItem } from "~~/types/global";

const appStore = useAppStore();
const authStore = useAuthStore();
const newArtStore = useNewArtworkStore();

const user = useState<AppUser | null>("auth-user", () => null);

const drawerRef = ref<HTMLElement | null>(null);
const localePath = useLocalePath();

const navItems = shallowRef<MenuItem[]>([
  {
    id: 0,
    key: "profile",
    title: "Profile",
    path: "/dashboard",
    icon: "tabler:user-filled",
  },
  {
    id: 1,
    title: "Favorites",
    key: "favorites",
    path: "/dashboard/favorites",
    icon: "tabler:heart-filled",
  },

  {
    id: 2,
    key: "my_artworks",
    title: "My artworks",
    path: "/dashboard/my-artworks",
    icon: "tabler:palette-filled",
  },
  {
    id: 3,
    key: "new_artwork",
    title: "New artwork",
    path: "/",
    icon: "tabler:circle-plus-filled",
  },
  {
    id: 4,
    key: "notifications",
    title: "Notifications",
    path: "/dashboard/notifications",
    icon: "tabler:bell-filled",
  },
  {
    id: 5,
    title: "Gallery",
    path: "/gallery",
    key: "gallery",
    icon: "tabler:photo-filled",
  },
  { id: 6, title: "Home", path: "/", key: "home", icon: "tabler:home-filled" },
]);

const navigate = async (item: MenuItem) => {
  if (item.id === 3) {
    appStore.closeDrawer();
    newArtStore.openNewArtwork();
    return;
  }
  const route = item.path;
  await navigateTo(localePath(route));
  appStore.closeDrawer();
};

const navigateNoAction = async (route: string) => {
  await navigateTo(localePath(route));
  appStore.closeDrawer();
};

onClickOutside(drawerRef, () => appStore.closeDrawer());
</script>
